/* eslint-disable filename-rules/match -- versioned token files are named after the DLS version */
/**
 * The DLS 2.2 type scale.
 *
 * Three families over one ramp of steps: `title` for headings, `display` for the same metrics
 * set quiet (large figures and balances), `body` for copy. The numeric names replace the 2.1
 * t-shirt names (`title-xs…jumbo-xl`, `body-xxl…xs`), which are on the way out — do not add new
 * ones to that set.
 *
 * **Weight is a prop, not part of the name.** Figma flattens it in (`body-500-bold`,
 * `title-400`…) because Figma has no props; here that is `textStyle="body-500"` +
 * `fontWeight="semibold"`. Figma calls 600 "Bold", so its `-bold` means `semibold` (600) and
 * never `bold` (700) — a weight Inter Republic cannot render. `title` is 600, `display` is 400,
 * `body` is 400 unless a `-medium` (500) or `-bold` (600) is asked for.
 *
 * Sizes and line-heights are literal rem rather than tokens. Four of the 2.2 font sizes (32, 40,
 * 44, 100px) and three of its line-heights (48, 68, 96px) have no token, and the t-shirt ladder
 * has no free slot to put them in. Naming them would mean inventing a third naming system for a
 * scale that is itself transitional. Tracking and weight do keep their tokens: every 2.2 value
 * already exists there exactly.
 *
 * Responsive steps are written `{ base, sm }` rather than as a two-element array. Chakra maps
 * array positions onto the ordered breakpoint keys — `base, xs, sm, …` — so `[mobile, desktop]`
 * would switch at `xs` (641px), and the 2.2 ramp steps down below 768px. `sm` is 769px, which is
 * the boundary the spec draws, give or take the single pixel at exactly 768. The 2.1 styles
 * above use the array form and switch at `xs`; that is their existing behaviour, not a model to
 * copy.
 *
 * Values follow the designer's prototype, which is ahead of Figma on the mobile ramp — Figma
 * still shows title-400 at 24/32, title-500 at 28/32 and title-600 at 32/36. Block 12 of the
 * `?content=typography` draft page holds the Figma figures for comparison; the difference is an
 * open question for the designer, not a mistake.
 *
 * `title-200` comes from the prototype alone: Figma defines a line-height for it but no font
 * size. It is the one step written flat where the prototype steps it — the prototype grows it to
 * 20px below 768px, the only step in that media query that goes up rather than down, which would
 * put 200, 300 and 400 all on 20/24 on a phone. Read as a slip and held at 18/24; that is the one
 * place this file knowingly departs from the prototype, and it goes to the designer with the
 * ramp question above.
 *
 * This file is split from `text-styles.ts` only while both generations coexist. When 2.1 retires
 * these become the whole of `textStyles` and the split goes away — see
 * `docs/adr/20260828-dls-22-typography-tokens.md`.
 */

/** `title` and `display` share one ramp; only the tracking (and the weight) differ. */
const TITLE_METRICS = {
  // Same at every width.
  50: { fontSize: '0.875rem', lineHeight: '1.25rem' }, // 14/20
  100: { fontSize: '1rem', lineHeight: '1.5rem' }, // 16/24
  200: { fontSize: '1.125rem', lineHeight: '1.5rem' }, // 18/24
  300: { fontSize: '1.25rem', lineHeight: '1.5rem' }, // 20/24

  // Steps down below `sm`. Comments read mobile | desktop.
  400: {
    fontSize: { base: '1.25rem', sm: '1.5rem' },
    lineHeight: { base: '1.5rem', sm: '2rem' },
  }, // 20/24 | 24/32
  500: {
    fontSize: { base: '1.5rem', sm: '1.75rem' },
    lineHeight: '2rem',
  }, // 24/32 | 28/32
  600: {
    fontSize: { base: '1.75rem', sm: '2.25rem' },
    lineHeight: { base: '2rem', sm: '2.5rem' },
  }, // 28/32 | 36/40
  700: {
    fontSize: { base: '2.25rem', sm: '3rem' },
    lineHeight: { base: '2.5rem', sm: '3rem' },
  }, // 36/40 | 48/48
  800: {
    fontSize: { base: '2.5rem', sm: '4.25rem' },
    lineHeight: { base: '2.5rem', sm: '4.25rem' },
  }, // 40/40 | 68/68
  900: {
    fontSize: { base: '2.75rem', sm: '6.25rem' },
    lineHeight: { base: '2.5rem', sm: '6rem' },
  }, // 44/40 | 100/96
} as const;

export const textStyles22 = {
  'title-50': { ...TITLE_METRICS[50], letterSpacing: 'normal' },
  'title-100': { ...TITLE_METRICS[100], letterSpacing: 'less-tight' },
  'title-200': { ...TITLE_METRICS[200], letterSpacing: 'less-tight' },
  'title-300': { ...TITLE_METRICS[300], letterSpacing: 'less-tight' },
  'title-400': { ...TITLE_METRICS[400], letterSpacing: 'tight' },
  'title-500': { ...TITLE_METRICS[500], letterSpacing: 'tight' },
  'title-600': { ...TITLE_METRICS[600], letterSpacing: 'tight' },
  'title-700': { ...TITLE_METRICS[700], letterSpacing: 'tighter' },
  'title-800': { ...TITLE_METRICS[800], letterSpacing: 'tighter' },
  'title-900': { ...TITLE_METRICS[900], letterSpacing: 'tighter' },

  // Display tracks looser than its title twin at 400 and tighter at 700; the rest match.
  'display-400': { ...TITLE_METRICS[400], letterSpacing: 'less-tight' },
  'display-500': { ...TITLE_METRICS[500], letterSpacing: 'tight' },
  'display-600': { ...TITLE_METRICS[600], letterSpacing: 'tight' },
  'display-700': { ...TITLE_METRICS[700], letterSpacing: 'tight' },
  'display-800': { ...TITLE_METRICS[800], letterSpacing: 'tighter' },
  'display-900': { ...TITLE_METRICS[900], letterSpacing: 'tighter' },

  // Body holds one size at every width. `-compact` is the same size on a tighter line, for
  // tables and anywhere text has to sit close.
  'body-200': {
    fontSize: '0.625rem',
    lineHeight: '0.75rem',
    letterSpacing: 'normal',
  }, // 10/12
  'body-300': {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    letterSpacing: 'normal',
  }, // 12/16
  'body-400': {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    letterSpacing: 'normal',
  }, // 14/20
  'body-500': {
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 'less-tight',
  }, // 16/24
  'body-600': {
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    letterSpacing: 'less-tight',
  }, // 18/24
  'body-700': {
    fontSize: '1.25rem',
    lineHeight: '1.5rem',
    letterSpacing: 'less-tight',
  }, // 20/24
  'body-400-compact': {
    fontSize: '0.875rem',
    lineHeight: '1rem',
    letterSpacing: 'normal',
  }, // 14/16
  'body-500-compact': {
    fontSize: '1rem',
    lineHeight: '1.25rem',
    letterSpacing: 'less-tight',
  }, // 16/20
};
